import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Settings, Plus, Edit, Trash2, Check, Loader2, Server, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMTPSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    smtp_server: "",
    smtp_port: 465,
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_from_name: "Delta AI Academy",
    is_active: false
  });

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/smtp/configs`);
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error("Failed to fetch SMTP configs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleTest = async () => {
    if (!formData.smtp_server || !formData.smtp_username || !formData.smtp_password) {
      toast.error("Please fill in server, username, and password");
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/smtp/test`, formData);
      if (response.data.success) {
        toast.success("Connection successful!");
      } else {
        toast.error(response.data.message || "Connection failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Connection failed");
    } finally {
      setTesting(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.smtp_server || !formData.smtp_username) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/smtp/configs`, formData);
      toast.success("SMTP configuration created");
      setDialogOpen(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedConfig) return;
    
    setSaving(true);
    try {
      // Don't send password if it's masked
      const updateData = {...formData};
      if (updateData.smtp_password === '********') {
        delete updateData.smtp_password;
      }
      
      await axios.put(`${API}/smtp/configs/${selectedConfig.id}`, updateData);
      toast.success("SMTP configuration updated");
      setDialogOpen(false);
      setSelectedConfig(null);
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this configuration?")) return;
    
    try {
      await axios.delete(`${API}/smtp/configs/${id}`);
      toast.success("Configuration deleted");
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete configuration");
    }
  };

  const handleSetActive = async (config) => {
    try {
      await axios.put(`${API}/smtp/configs/${config.id}`, { is_active: true });
      toast.success(`${config.name} is now active`);
      fetchConfigs();
    } catch (error) {
      toast.error("Failed to set as active");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      smtp_server: "",
      smtp_port: 465,
      smtp_username: "",
      smtp_password: "",
      smtp_from_email: "",
      smtp_from_name: "Delta AI Academy",
      is_active: false
    });
  };

  const openEditDialog = (config) => {
    setSelectedConfig(config);
    setFormData({
      name: config.name,
      smtp_server: config.smtp_server,
      smtp_port: config.smtp_port,
      smtp_username: config.smtp_username,
      smtp_password: config.smtp_password,
      smtp_from_email: config.smtp_from_email,
      smtp_from_name: config.smtp_from_name,
      is_active: config.is_active
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setSelectedConfig(null);
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="smtp-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            SMTP Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure your email server connections
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg w-fit"
          data-testid="btn-add-smtp"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add SMTP Server
        </Button>
      </div>

      {/* Configs List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading configurations...</div>
      ) : configs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configs.map((config) => (
            <Card key={config.id} className={`bg-[#111] border-[#1a1a1a] rounded-lg ${config.is_active ? 'ring-1 ring-cyan-500/50' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <Server className="w-5 h-5 text-cyan-400" />
                      {config.name}
                      {config.is_active && (
                        <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Server</p>
                    <p className="text-slate-300 font-mono">{config.smtp_server}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Port</p>
                    <p className="text-slate-300 font-mono">{config.smtp_port}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Username</p>
                    <p className="text-slate-300 font-mono text-xs truncate">{config.smtp_username}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">From Name</p>
                    <p className="text-slate-300">{config.smtp_from_name}</p>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  {!config.is_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetActive(config)}
                      className="flex-1 border-[#222] text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                      data-testid={`btn-activate-${config.id}`}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Set Active
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(config)}
                    className="flex-1 border-[#222] text-slate-400 hover:text-white hover:bg-[#1a1a1a]"
                    data-testid={`btn-edit-${config.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(config.id)}
                    className="border-[#222] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`btn-delete-${config.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
          <CardContent className="py-12 text-center">
            <Settings className="w-12 h-12 mx-auto text-slate-700 mb-3" />
            <p className="text-slate-500">No SMTP configurations yet.</p>
            <p className="text-slate-600 text-sm mt-1">Add your first SMTP server to start sending emails.</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#111] border-[#1a1a1a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedConfig ? "Edit SMTP Configuration" : "Add SMTP Server"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-400">Configuration Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Primary Server"
                className="bg-[#0d0d0d] border-[#222] text-white"
                data-testid="input-smtp-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400">SMTP Server *</Label>
                <Input
                  value={formData.smtp_server}
                  onChange={(e) => setFormData({...formData, smtp_server: e.target.value})}
                  placeholder="smtp.example.com"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-smtp-server"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Port *</Label>
                <Input
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({...formData, smtp_port: parseInt(e.target.value)})}
                  placeholder="465"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-smtp-port"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400">Username *</Label>
              <Input
                value={formData.smtp_username}
                onChange={(e) => setFormData({...formData, smtp_username: e.target.value})}
                placeholder="your@email.com"
                className="bg-[#0d0d0d] border-[#222] text-white"
                data-testid="input-smtp-username"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400">Password *</Label>
              <Input
                type="password"
                value={formData.smtp_password}
                onChange={(e) => setFormData({...formData, smtp_password: e.target.value})}
                placeholder="••••••••"
                className="bg-[#0d0d0d] border-[#222] text-white"
                data-testid="input-smtp-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400">From Email</Label>
                <Input
                  value={formData.smtp_from_email}
                  onChange={(e) => setFormData({...formData, smtp_from_email: e.target.value})}
                  placeholder="noreply@example.com"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-smtp-from-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">From Name</Label>
                <Input
                  value={formData.smtp_from_name}
                  onChange={(e) => setFormData({...formData, smtp_from_name: e.target.value})}
                  placeholder="Delta AI Academy"
                  className="bg-[#0d0d0d] border-[#222] text-white"
                  data-testid="input-smtp-from-name"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="rounded border-[#222] bg-[#0d0d0d]"
              />
              <Label htmlFor="is_active" className="text-slate-400 cursor-pointer">
                Set as active (use for sending)
              </Label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing}
                className="border-[#222] text-slate-400 hover:text-white"
                data-testid="btn-test-smtp"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Test Connection
              </Button>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#222] text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={selectedConfig ? handleUpdate : handleCreate}
                disabled={saving}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="btn-save-smtp"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {selectedConfig ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMTPSettings;
